import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, Send, ArrowRight, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  useEffect(() => {
    document.title = "Contact Us | 707-384-3409 | Virgil's Heating & Air";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Contact Virgil's Heating & Air for HVAC services in Greater Sacramento. Call 707-384-3409 or email heyvirgil@virgilshvac.com. 24/7 emergency service available. Licensed CLSB# 1054485.");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you shortly.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (e) {
      console.error("Error sending message:", e);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-36 min-h-screen bg-[#050505]" data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden bg-[#0a0a0a]" data-testid="contact-hero">
        <div
          className="absolute inset-0 bg-cover bg-center img-sharp"
          style={{
            backgroundImage: "url(https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/qywdc139_A3A52283-E6E6-4617-9789-FA08DE430F41.png)",
            filter: "brightness(1.6) contrast(1.03) saturate(1.08)"
          }}
          data-testid="contact-hero-bg"
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/85 via-[#050505]/55 to-[#050505]/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent"></div>
        <div className="container-custom relative z-10">
          <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Contact Us</p>
          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl gradient-text mb-4">
            GET IN TOUCH
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl">
            Have questions or need service? We're here to help. 
            Reach out to us and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div data-testid="contact-info">
              <h2 className="font-heading font-bold text-2xl text-white mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">
                {/* Phone */}
                <a 
                  href="tel:7073843409" 
                  className="flex items-start gap-4 p-6 bg-gradient-to-r from-[#0a0a12] to-[#0a0a0a] rounded-2xl hover:shadow-lg transition-all group"
                  data-testid="contact-phone"
                >
                  <div className="p-4 bg-gradient-to-r from-[#0066CC] to-[#1E40AF] rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Phone</p>
                    <p className="font-heading font-bold text-2xl text-white">707-384-3409</p>
                    <p className="text-[#5b93ff] text-sm mt-1 font-medium">Available 24/7 for emergencies</p>
                  </div>
                </a>

                {/* Email */}
                <a 
                  href="mailto:heyvirgil@virgilshvac.com" 
                  className="flex items-start gap-4 p-6 bg-[#101014] rounded-2xl border-2 border-white/10 hover:border-[#0066CC] hover:shadow-lg transition-all group"
                  data-testid="contact-email"
                >
                  <div className="p-4 bg-[#1E6BFF]/10 group-hover:bg-gradient-to-r group-hover:from-[#0066CC] group-hover:to-[#1E40AF] rounded-xl transition-all">
                    <Mail className="w-6 h-6 text-[#5b93ff] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Email</p>
                    <p className="font-heading font-bold text-xl text-white">heyvirgil@virgilshvac.com</p>
                    <p className="text-gray-400 text-sm mt-1">We'll respond within 24 hours</p>
                  </div>
                </a>

                {/* Location */}
                <div 
                  className="flex items-start gap-4 p-6 bg-[#101014] rounded-2xl border-2 border-white/10"
                  data-testid="contact-location"
                >
                  <div className="p-4 bg-[#1E6BFF]/10 rounded-xl">
                    <MapPin className="w-6 h-6 text-[#5b93ff]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Service Area</p>
                    <p className="font-heading font-bold text-xl text-white">Greater Sacramento Area</p>
                    <p className="text-gray-400 text-sm mt-1">And surrounding communities</p>
                  </div>
                </div>

                {/* Hours */}
                <div 
                  className="flex items-start gap-4 p-6 bg-[#101014] rounded-2xl border-2 border-white/10"
                  data-testid="contact-hours"
                >
                  <div className="p-4 bg-[#1E6BFF]/10 rounded-xl">
                    <Clock className="w-6 h-6 text-[#5b93ff]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Business Hours</p>
                    <p className="font-heading font-bold text-lg text-white">Mon - Fri: 8AM - 6PM</p>
                    <p className="text-white">Sat: 9AM - 4PM</p>
                    <p className="text-[#ff6a5c] text-sm mt-2 font-bold">🔥 24/7 Emergency Service Available</p>
                  </div>
                </div>

                {/* License */}
                <div 
                  className="flex items-start gap-4 p-6 bg-[#101014] rounded-2xl border-2 border-white/10"
                  data-testid="contact-license"
                >
                  <div className="p-4 bg-[#1E6BFF]/10 rounded-xl">
                    <Shield className="w-6 h-6 text-[#5b93ff]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">License</p>
                    <p className="font-mono font-bold text-xl text-white">CLSB# 1054485</p>
                    <p className="text-gray-400 text-sm mt-1">Licensed & Insured</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#101014] rounded-3xl shadow-xl p-8 border-2 border-white/10" data-testid="contact-form">
              <h2 className="font-heading font-bold text-2xl text-white mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-white mb-2 block font-semibold">Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      className="input-light h-14 text-lg"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block font-semibold">Phone</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(707) 000-0000"
                      className="input-light h-14 text-lg"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block font-semibold">Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="input-light h-14 text-lg"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label className="text-white mb-2 block font-semibold">Message *</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="How can we help you?"
                    className="input-light min-h-[180px] text-lg"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full text-lg py-6"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#DC2626] relative overflow-hidden" data-testid="contact-cta">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="font-heading font-bold text-4xl md:text-5xl mb-4 drop-shadow-lg">
              NEED IMMEDIATE ASSISTANCE?
            </h2>
            <p className="text-white/90 text-xl mb-8">
              For urgent heating or cooling issues, call us directly. 
              Our technicians are available 24/7 for emergency service.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:7073843409">
                <Button className="bg-white text-[#FF2D1A] hover:bg-gray-100 font-bold text-xl px-10 py-6 rounded-full shadow-xl" data-testid="emergency-call-btn">
                  <Phone className="mr-2 w-6 h-6" />
                  Call Now: 707-384-3409
                </Button>
              </a>
              <Link to="/booking">
                <Button className="bg-[#0066CC] hover:bg-[#1E40AF] text-white font-bold text-xl px-10 py-6 rounded-full shadow-xl" data-testid="schedule-btn">
                  Schedule Service
                  <ArrowRight className="ml-2 w-6 h-6" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
